import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight, Sparkles, BookOpen, PenLine } from "lucide-react";
import PostThumb from "@/components/PostThumb";
import Seo from "@/components/Seo";

const HERO_IMG =
  "https://images.unsplash.com/photo-1491406213019-05b162a72c20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzg2NzIzMjE0fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [posts, setPosts] = useState([]);
  const [thoughts, setThoughts] = useState([]);

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => setPosts([]));
    api.get("/thoughts").then((r) => setThoughts(r.data)).catch(() => setThoughts([]));
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1, 4);
  const recentThoughts = thoughts.slice(0, 3);

  return (
    <main data-testid="landing-page">
      <Seo path="/" />
      {/* HERO — asymmetric editorial */}
      <section className="relative overflow-hidden border-b border-border/60 grain-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-foreground/60 mb-10">
              <span className="h-px w-8 bg-accent" />
              Miscellaneous thoughts on money and related fields
            </div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-[5.5rem] font-light leading-[1.02] tracking-tight italic">
              “Money is an incredible invention.
              <br />
              Understanding it is anything <span className="not-italic">but</span> simple.”
            </h1>
            <p className="mt-10 max-w-xl text-lg text-foreground/70 leading-relaxed">
              A sincere space where I collect, organize, and share the ideas I explore
              every day — on finance, decision-making, and the mindset behind them.
              Thoughts, essays, and the tools I actually use.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-4">
              <Link
                to="/thoughts"
                data-testid="hero-cta-thoughts"
                className="group inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-primary/90 transition-colors"
              >
                Read the thoughts
                <ArrowUpRight
                  size={16}
                  strokeWidth={1.5}
                  className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </Link>
              <Link
                to="/about"
                data-testid="hero-cta-about"
                className="inline-flex items-center gap-2 rounded-full border border-foreground/25 px-6 py-3 text-xs uppercase tracking-[0.2em] hover:border-primary hover:text-primary transition-colors"
              >
                About me
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative animate-fade-up">
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm border border-border">
              <img
                src={HERO_IMG}
                alt="Library building against sky"
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/50 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-primary-foreground">
                <div className="text-[10px] uppercase tracking-[0.3em] opacity-80">
                  An ordered journal
                </div>
                <div className="font-serif text-2xl mt-2 leading-tight italic">
                  Nothing more, nothing less: just a sincere space where I share
                  the journey.
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block w-40 h-40 bg-accent/15 rounded-full blur-2xl -z-0" />
          </div>
        </div>
      </section>

      {/* THOUGHTS PREVIEW */}
      {recentThoughts.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 border-b border-border/60">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60 mb-4">
                Latest thoughts
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tighter leading-[1.05]">
                Short entries.
                <br />
                <span className="italic text-primary">Long shadows.</span>
              </h2>
              <p className="mt-6 text-foreground/70 leading-relaxed max-w-xs">
                A running commonplace book: quotes I keep coming back to, and the
                small reflections they trigger.
              </p>
              <Link
                to="/thoughts"
                data-testid="thoughts-preview-see-all"
                className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary hover:opacity-80 transition-opacity"
              >
                See the full log <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
            <div className="lg:col-span-8 space-y-10">
              {recentThoughts.map((t) => (
                <ThoughtPreview key={t.thought_id} t={t} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FUTURE PRODUCTS — Bento */}
      <section id="future" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60 mb-4">
              What's growing here
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tighter max-w-2xl">
              One home for the tools, essays and services I keep meaning to build.
            </h2>
          </div>
          <p className="max-w-sm text-sm text-foreground/60 leading-relaxed">
            Today: Thoughts, Journal and a small workbench of financial tools.
            More will land here as it is ready.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="bento-grid">
          <BentoCard
            testId="bento-thoughts"
            span="md:col-span-7 md:row-span-2"
            tag="Live"
            tagStyle="bg-primary text-primary-foreground"
            title="Thoughts"
            body="Dated micro-entries — a quote that stopped me, and the reflection it triggered. The heart of Moneyssance."
            icon={<PenLine size={20} strokeWidth={1.5} />}
            href="/thoughts"
            internal
            tall
          />
          <BentoCard
            testId="bento-journal"
            span="md:col-span-5 md:row-span-1"
            tag="Live"
            tagStyle="bg-primary text-primary-foreground"
            title="Journal"
            body="Long-form essays for when a thought grows past a paragraph."
            icon={<BookOpen size={20} strokeWidth={1.5} />}
            href="/blog"
            internal
          />
          <BentoCard
            testId="bento-tools"
            span="md:col-span-5 md:row-span-1"
            tag="Live"
            tagStyle="bg-primary text-primary-foreground"
            title="Financial Tools"
            body="A small workbench of calculators I use daily. Compound interest is up — more shipping progressively."
            icon={<Sparkles size={20} strokeWidth={1.5} />}
            href="/tools"
            internal
          />
        </div>
      </section>

      {/* JOURNAL PREVIEW — only if posts exist */}
      {posts.length > 0 && (
        <section className="border-t border-border/60 bg-secondary/30">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
            <div className="flex items-end justify-between mb-14">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60 mb-4">
                  From the journal
                </div>
                <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tighter">
                  Recently written.
                </h2>
              </div>
              <Link
                to="/blog"
                data-testid="journal-preview-see-all"
                className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] hover:text-primary transition-colors"
              >
                See all <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              {featured && (
                <Link
                  to={`/blog/${featured.slug}`}
                  data-testid={`featured-post-${featured.slug}`}
                  className="lg:col-span-7 group block"
                >
                  <PostThumb post={featured} className="aspect-[16/10]" />
                  <div className="mt-6 text-[10px] uppercase tracking-[0.25em] text-accent">
                    {featured.category} · {featured.read_minutes} min read
                  </div>
                  <h3 className="mt-3 font-serif text-3xl md:text-4xl leading-tight tracking-tight group-hover:text-primary transition-colors">
                    {featured.title}
                  </h3>
                  <p className="mt-4 text-foreground/70 leading-relaxed max-w-2xl">
                    {featured.excerpt}
                  </p>
                </Link>
              )}
              <div className="lg:col-span-5 flex flex-col gap-8">
                {rest.map((p) => (
                  <Link
                    key={p.post_id}
                    to={`/blog/${p.slug}`}
                    data-testid={`preview-post-${p.slug}`}
                    className="group grid grid-cols-5 gap-5 items-start border-b border-border/60 pb-8 last:border-0"
                  >
                    <div className="col-span-2">
                      <PostThumb post={p} className="aspect-[4/3]" />
                    </div>
                    <div className="col-span-3">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-accent">
                        {p.category} · {p.read_minutes} min
                      </div>
                      <div className="mt-2 font-serif text-xl leading-snug group-hover:text-primary transition-colors">
                        {p.title}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}

function ThoughtPreview({ t }) {
  const date = formatEntryDate(t.entry_date);
  return (
    <Link
      to="/thoughts"
      data-testid={`thought-preview-${t.thought_id}`}
      className="block group border-b border-border/60 pb-8 last:border-0"
    >
      <div className="text-[10px] uppercase tracking-[0.3em] text-accent">{date}</div>
      {t.quote && (
        <blockquote className="mt-4 border-l-2 border-primary pl-6 font-serif text-2xl italic leading-snug text-foreground/90 group-hover:text-primary transition-colors">
          “{t.quote}”
          {t.quote_source && (
            <span className="block mt-2 not-italic text-xs uppercase tracking-widest text-foreground/50">
              — {t.quote_source}
            </span>
          )}
        </blockquote>
      )}
      {t.reflection && (
        <p className="mt-4 text-foreground/70 leading-relaxed line-clamp-3">
          {t.reflection}
        </p>
      )}
    </Link>
  );
}

function formatEntryDate(iso) {
  if (!iso) return "";
  // Accept both YYYY-MM-DD and DD.MM.YYYY
  const parts = iso.includes(".") ? iso.split(".").reverse() : iso.split("-");
  const d = new Date(`${parts[0]}-${parts[1] || "01"}-${parts[2] || "01"}`);
  if (isNaN(d)) return iso;
  return d
    .toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
    .replace(/\//g, ".");
}

function BentoCard({ testId, span, tag, tagStyle, title, body, icon, href, internal, tall }) {
  const Wrapper = href && internal ? Link : href ? "a" : "div";
  const linkProps = href && internal ? { to: href } : href ? { href } : {};
  return (
    <Wrapper
      {...linkProps}
      data-testid={testId}
      className={`bento-card ${span} relative flex flex-col justify-between border border-border rounded-sm p-8 md:p-10 bg-background hover:border-primary ${
        tall ? "min-h-[420px]" : "min-h-[220px]"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-full border border-border flex items-center justify-center text-primary">
          {icon}
        </div>
        <span
          className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border border-border ${
            tagStyle || "text-foreground/70"
          }`}
        >
          {tag}
        </span>
      </div>
      <div className="mt-10">
        <h3 className={`font-serif ${tall ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"} tracking-tight leading-tight`}>
          {title}
        </h3>
        <p className={`mt-4 text-foreground/70 leading-relaxed ${tall ? "text-lg max-w-md" : "text-sm max-w-sm"}`}>
          {body}
        </p>
        {href && (
          <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary">
            Enter <ArrowUpRight size={14} strokeWidth={1.5} />
          </div>
        )}
      </div>
    </Wrapper>
  );
}
